import React, { useCallback, useMemo } from "react";
import { IconButton, Paper, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Add as AddIcon, Check as CheckIcon } from "@material-ui/icons";
import { ActiveStatus } from "src/apps/commons/dataDisplays";
import { DataTable } from "src/apps/commons/dataTable";
import * as Api from "../../remyndProduct/api";
import ListFilterDialog from "../../remyndProduct/components/ListFilterDialog";
import {
  IRemyndProduct,
  IRemyndProductPaginateReq,
  RemyndProductPaginateOptionKey
} from "../../remyndProduct/types";
import { constants } from "../../remyndProduct/utils";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    backgroundColor: theme.palette.background.default
  }
}));

interface Props {
  excludeIds: string[];
  onAdd: (product: IRemyndProduct) => void;
}

const SelectableRemyndProductList: React.FC<Props> = props => {
  const { excludeIds, onAdd } = props;
  const classes = useStyles();

  const ManageCell = useCallback(
    (data: IRemyndProduct, isLoading: boolean) => (
      <div>
        {isLoading ? (
          <IconButton
            aria-label="Add"
            key={`loading_manage_${data.id}`}
            disabled
          >
            <AddIcon />
          </IconButton>
        ) : !excludeIds.some(d => d == data.id) ? (
          <IconButton aria-label="Add" onClick={() => onAdd(data)}>
            <Tooltip title={`เพิ่ม ${constants.label}`} placement="top">
              <AddIcon />
            </Tooltip>
          </IconButton>
        ) : (
          <IconButton aria-label="Complete" disabled>
            <CheckIcon />
          </IconButton>
        )}
      </div>
    ),
    [excludeIds, onAdd]
  );
  const ActiveCell = useCallback(
    (data: IRemyndProduct) => <ActiveStatus value={data.active} />,
    []
  );

  const defaultCriteria = useMemo(
    () =>
      ({
        paginate: {
          page: 1,
          pageSize: 10,
          order: { optionKey: "NAME_TH", desc: false }
        }
      } as IRemyndProductPaginateReq),
    []
  );

  return (
    <Paper className={classes.root}>
      <DataTable<
        IRemyndProduct,
        IRemyndProductPaginateReq,
        RemyndProductPaginateOptionKey,
        "MANAGE"
      >
        searchTermLabel={"ค้นหาจาก รหัส, ชื่อ, แบรนด์, หมวดสินค้า"}
        listEntity={Api.list}
        filterDialog={ListFilterDialog}
        defaultCriteria={defaultCriteria}
        columns={[
          {
            id: "MANAGE",
            label: "จัดการ",
            width: 80,
            align: "center",
            renderCell: ManageCell
          },
          {
            id: "CODE",
            label: "รหัส",
            minWidth: 150,
            orderable: true,
            renderCell: data => data.code ?? ""
          },
          {
            id: "NAME_TH",
            label: "ชื่อ (TH)",
            minWidth: 300,
            orderable: true,
            renderCell: data => data.nameTh
          },
          {
            id: "NAME_EN",
            label: "ชื่อ (EN)",
            minWidth: 300,
            orderable: true,
            renderCell: data => data.nameEn
          },
          {
            id: "BRAND",
            label: "แบรนด์",
            minWidth: 150,
            orderable: true,
            renderCell: data => data.brand?.nameTh ?? "ไม่ระบุ",
            renderFaded: data => !data.brand?.nameTh
          },
          {
            id: "CAT_LV1",
            label: "หมวดสินค้า Lv1",
            minWidth: 200,
            orderable: true,
            renderCell: data => data.categoryLv1?.nameTh ?? "ไม่ระบุ",
            renderFaded: data => !data.categoryLv1?.nameTh
          },
          {
            id: "CAT_LV2",
            label: "หมวดสินค้า Lv2",
            minWidth: 200,
            orderable: true,
            renderCell: data => data.categoryLv2?.nameTh ?? "ไม่ระบุ",
            renderFaded: data => !data.categoryLv2?.nameTh
          },
          {
            id: "CAT_LV3",
            label: "หมวดสินค้า Lv3",
            minWidth: 200,
            orderable: true,
            renderCell: data => data.categoryLv3?.nameTh ?? "ไม่ระบุ",
            renderFaded: data => !data.categoryLv3?.nameTh
          },
          {
            id: "ACTIVE",
            label: "Active",
            width: 80,
            orderable: true,
            align: "center",
            renderCell: ActiveCell
          }
        ]}
      />
    </Paper>
  );
};

export default SelectableRemyndProductList;
